import React, { useEffect, useRef, useState } from 'react'

interface CounterProps {
  target: number
  duration?: number
  className?: string
}

const Counter: React.FC<CounterProps> = ({ target, duration = 2000, className = '' }) => {
  const ref = useRef<HTMLSpanElement>(null)
  const [value, setValue] = useState(0)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setValue(target)
      return
    }
    let raf = 0
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        observer.disconnect() // once
        const start = performance.now()
        const tick = (now: number) => {
          const progress = Math.min((now - start) / duration, 1)
          const eased = 1 - Math.pow(1 - progress, 3) // ease-out cubic
          setValue(Math.round(eased * target))
          if (progress < 1) raf = requestAnimationFrame(tick)
        }
        raf = requestAnimationFrame(tick)
      },
      // Bottom inset: counting starts only once the number has travelled
      // 20% up into the viewport — not the moment it grazes the bottom edge.
      { threshold: 0, rootMargin: '0px 0px -20% 0px' }
    )
    observer.observe(el)
    return () => {
      observer.disconnect()
      cancelAnimationFrame(raf)
    }
  }, [target, duration])

  return (
    <span ref={ref} className={className}>
      {value}
    </span>
  )
}

export default Counter
